import React from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import ColorSelectPopover from './ColorSelectPopover';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const InvoiceTable = ({
  rows, products, updateRow, selectDesign, removeRow, addRow,
  filteredDesigns, getBrandName, handleTableKeyDown, setRows, setCostHistoryPopup,
  grandQty, grandTotal,
}) => (
  <Card>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="pe-invoice-table">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left w-12 font-semibold text-gray-600">Sr</th>
              <th className="px-3 py-2 text-left min-w-[130px] font-semibold text-gray-600">Design No</th>
              <th className="px-3 py-2 text-left min-w-[120px] font-semibold text-gray-600">Colour</th>
              <th className="px-3 py-2 text-left w-24 font-semibold text-gray-600">Size</th>
              <th className="px-3 py-2 text-right w-20 font-semibold text-gray-600">Qty</th>
              <th className="px-3 py-2 text-right w-28 font-semibold text-gray-600">Cost</th>
              <th className="px-3 py-2 text-right w-28 font-semibold text-gray-600">
                WSP (L) <span className="text-red-500">*</span>
              </th>
              <th className="px-3 py-2 text-right w-28 font-semibold text-gray-600">
                WSP <span className="text-red-500">*</span>
              </th>
              <th className="px-3 py-2 text-right w-24 font-semibold text-gray-600">Amount</th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-b hover:bg-gray-50/50" data-testid={`pe-row-${idx}`}>
                <td className="px-3 py-1.5 text-gray-500 font-mono">{row.sr}</td>
                <td className="px-3 py-1.5 relative" data-pe-design-cell>
                  <Input value={row.designSearch}
                    onChange={e => {
                      const val = e.target.value;
                      updateRow(idx, 'designSearch', val);
                      updateRow(idx, 'showDesignDropdown', true);
                      // Auto-select on exact match
                      if (val.trim()) {
                        const exact = products.find(p => p.design_code.toLowerCase() === val.trim().toLowerCase());
                        if (exact) selectDesign(idx, exact);
                      }
                    }}
                    onFocus={() => { if (row.designSearch) updateRow(idx, 'showDesignDropdown', true); }}
                    placeholder="Search design..." className="h-8 text-sm" data-testid={`pe-design-search-${idx}`}
                    data-row={idx} data-col={0} onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const matches = filteredDesigns(row.designSearch);
                        if (matches.length === 1) selectDesign(idx, matches[0]);
                      }
                      handleTableKeyDown(e);
                    }}
                  />
                  {row.design_code && <Badge className="absolute right-4 top-2.5 bg-blue-100 text-blue-700 text-xs">{getBrandName(row.product?.brand_id)}</Badge>}
                  {row.productMissing && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-600" data-testid={`pe-design-missing-${idx}`}>
                      <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                      <span>Design no longer in catalogue — colour/size options limited to what was saved.</span>
                    </div>
                  )}
                  {row.showDesignDropdown && row.designSearch && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto left-0">
                      {filteredDesigns(row.designSearch).map(p => (
                        <div key={p.id} className="px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm flex items-center gap-2"
                          onMouseDown={(e) => { e.preventDefault(); selectDesign(idx, p); }} data-testid={`pe-design-option-${p.id}`}>
                          {p.images?.[0] && <img src={`${API_BASE}${p.images[0]}`} alt="" className="w-8 h-8 rounded object-cover" />}
                          <span className="font-mono font-semibold">{p.design_code}</span>
                          <span className="text-gray-400 text-xs">{p.category}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <ColorSelectPopover idx={idx} row={row} onChange={updateRow} disabled={!row.product_id} testIdPrefix="pe" />
                </td>
                <td className="px-3 py-1.5">
                  <select value={row.size} onChange={e => updateRow(idx, 'size', e.target.value)}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                    disabled={!row.color} data-testid={`pe-size-${idx}`}
                    data-row={idx} data-col={2} onKeyDown={handleTableKeyDown}>
                    <option value="">Select</option>
                    {(row.sizeGroups || []).length > 1 && (() => {
                      const allSizes = (row.sizeGroups || []).flatMap(g => g.sizes);
                      return <option value="both">{allSizes[0]} to {allSizes[allSizes.length - 1]} ({allSizes.length}pcs)</option>;
                    })()}
                    {(row.sizeGroups || []).length > 0 && <optgroup label="Sets">
                      {(row.sizeGroups || []).map(g => <option key={g.id} value={g.id}>{g.label} ({g.count}pcs)</option>)}
                    </optgroup>}
                    <optgroup label="Individual">
                      {(row.individualSizes || []).map(s => <option key={s.size} value={s.size}>{s.size}</option>)}
                    </optgroup>
                  </select>
                </td>
                <td className="px-3 py-1.5">
                  <Input type="number" min="0" value={row.qty} onChange={e => updateRow(idx, 'qty', e.target.value)}
                    className="h-8 text-right text-sm" onWheel={e => e.target.blur()} data-testid={`pe-qty-${idx}`}
                    data-row={idx} data-col={3} onKeyDown={handleTableKeyDown} />
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1 relative">
                    <Input type="number" min="0" step="0.01" value={row.cost} onChange={e => updateRow(idx, 'cost', e.target.value)}
                      className="h-8 text-right text-sm flex-1" onWheel={e => e.target.blur()} data-testid={`pe-cost-${idx}`}
                      data-row={idx} data-col={4} onKeyDown={handleTableKeyDown} />
                    {/* Cost change warning badge */}
                    {row.cost > 0 && row._prevPricing && (() => {
                      const currentSize = row.size;
                      const grp = (row.sizeGroups || []).find(g => g.id === currentSize);
                      const lookupSize = grp ? grp.sizes[0] : currentSize;
                      const prevCost = row._prevPricing[lookupSize]?.cost;
                      if (prevCost !== undefined && prevCost > 0 && parseFloat(row.cost) !== prevCost) {
                        const pct = (((parseFloat(row.cost) - prevCost) / prevCost) * 100).toFixed(1);
                        const isUp = parseFloat(row.cost) > prevCost;
                        return (
                          <button onClick={() => setCostHistoryPopup({ idx, design_code: row.design_code, history: row._costHistory || [] })}
                            className={`shrink-0 px-1 py-0.5 rounded text-[9px] font-bold border cursor-pointer ${isUp ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-600 border-green-200'}`}
                            title={`Was ₹${prevCost} in last PE. Click for history.`}
                            data-testid={`cost-change-badge-${idx}`}>
                            {isUp ? '▲' : '▼'}{Math.abs(pct)}%
                          </button>
                        );
                      }
                      return null;
                    })()}
                    {row.cost > 0 && <button title={`Copy Cost to same product/color rows`} onClick={() => {
                      const grp = (row.sizeGroups || []).find(g => g.id === row.size);
                      setRows(prev => prev.map(r => {
                        if (r.product_id !== row.product_id || r.color !== row.color) return r;
                        if (grp) {
                          // Set mode: copy to rows whose size is in this set
                          if (grp.sizes.includes(r.size)) {
                            const q = parseFloat(r.qty) || 0; const c = parseFloat(row.cost) || 0;
                            return { ...r, cost: row.cost, amount: Math.round(q * c) };
                          }
                          const rGrp = (r.sizeGroups || []).find(g => g.id === r.size);
                          if (rGrp && rGrp.sizes.some(s => grp.sizes.includes(s))) {
                            const q = parseFloat(r.qty) || 0; const c = parseFloat(row.cost) || 0;
                            const mult = rGrp ? rGrp.count : 1;
                            return { ...r, cost: row.cost, amount: Math.round(q * mult * c) };
                          }
                          return r;
                        }
                        // Individual size: copy to ALL rows of same product+color
                        const q = parseFloat(r.qty) || 0; const c = parseFloat(row.cost) || 0;
                        const rGrp = (r.sizeGroups || []).find(g => g.id === r.size);
                        const mult = rGrp ? rGrp.count : 1;
                        return { ...r, cost: row.cost, amount: Math.round(q * mult * c) };
                      }));
                      toast.success(`Cost ₹${row.cost} copied`);
                    }} className="text-blue-500 hover:text-blue-700 shrink-0" data-testid={`copy-cost-${idx}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    </button>}
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  {row.size === 'both' ? (
                    <div className="text-right text-xs leading-tight">
                      {(row.sizeGroups || []).map(g => <div key={g.id} className="text-gray-700"><span className="text-gray-400">{g.sizes[0]}-{g.sizes[g.sizes.length-1]}:</span> ₹{g.wsp}</div>)}
                    </div>
                  ) : (
                  <div className="flex items-center gap-1">
                    <Input type="number" min="0" step="0.01" value={row.wsp_local} onChange={e => updateRow(idx, 'wsp_local', e.target.value)}
                      className={`h-8 text-right text-sm flex-1 ${!row.wsp_local || parseFloat(row.wsp_local) <= 0 ? 'border-red-300 bg-red-50/50' : ''}`} onWheel={e => e.target.blur()} data-testid={`pe-wsp-local-${idx}`}
                      data-row={idx} data-col={5} onKeyDown={handleTableKeyDown} />
                    {row.wsp_local > 0 && <button title={`Copy WSP (L) to same product/color rows`} onClick={() => {
                      const grp = (row.sizeGroups || []).find(g => g.id === row.size);
                      setRows(prev => prev.map(r => {
                        if (r.product_id !== row.product_id || r.color !== row.color) return r;
                        if (grp) {
                          if (grp.sizes.includes(r.size)) return { ...r, wsp_local: row.wsp_local };
                          const rGrp = (r.sizeGroups || []).find(g => g.id === r.size);
                          if (rGrp && rGrp.sizes.some(s => grp.sizes.includes(s))) return { ...r, wsp_local: row.wsp_local };
                          return r;
                        }
                        return { ...r, wsp_local: row.wsp_local };
                      }));
                      toast.success(`WSP (L) ₹${row.wsp_local} copied`);
                    }} className="text-blue-500 hover:text-blue-700 shrink-0" data-testid={`copy-wsp-local-${idx}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    </button>}
                  </div>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  {row.size === 'both' ? (
                    <div className="text-right text-xs leading-tight">
                      {(row.sizeGroups || []).map(g => <div key={g.id} className="text-gray-700"><span className="text-gray-400">{g.sizes[0]}-{g.sizes[g.sizes.length-1]}:</span> ₹{g.mrp}</div>)}
                    </div>
                  ) : (
                  <div className="flex items-center gap-1">
                    <Input type="number" min="0" step="0.01" value={row.wsp_all} onChange={e => updateRow(idx, 'wsp_all', e.target.value)}
                      className={`h-8 text-right text-sm flex-1 ${!row.wsp_all || parseFloat(row.wsp_all) <= 0 ? 'border-red-300 bg-red-50/50' : ''}`} onWheel={e => e.target.blur()} data-testid={`pe-wsp-all-${idx}`}
                      data-row={idx} data-col={6} onKeyDown={handleTableKeyDown} />
                    {row.wsp_all > 0 && <button title={`Copy WSP to same product/color rows`} onClick={() => {
                      const grp = (row.sizeGroups || []).find(g => g.id === row.size);
                      setRows(prev => prev.map(r => {
                        if (r.product_id !== row.product_id || r.color !== row.color) return r;
                        if (grp) {
                          if (grp.sizes.includes(r.size)) return { ...r, wsp_all: row.wsp_all };
                          const rGrp = (r.sizeGroups || []).find(g => g.id === r.size);
                          if (rGrp && rGrp.sizes.some(s => grp.sizes.includes(s))) return { ...r, wsp_all: row.wsp_all };
                          return r;
                        }
                        return { ...r, wsp_all: row.wsp_all };
                      }));
                      toast.success(`WSP ₹${row.wsp_all} copied`);
                    }} className="text-blue-500 hover:text-blue-700 shrink-0" data-testid={`copy-wsp-all-${idx}`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    </button>}
                  </div>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right font-semibold text-gray-900">
                  {row.amount > 0 ? `₹${row.amount.toLocaleString('en-IN')}` : '-'}
                </td>
                <td className="px-3 py-1.5">
                  {rows.length > 1 && (
                    <button onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600" data-testid={`pe-remove-row-${idx}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t-2">
            <tr>
              <td colSpan="4" className="px-3 py-2">
                <Button variant="outline" size="sm" onClick={addRow} className="text-xs" data-testid="pe-add-row">
                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add Row
                </Button>
              </td>
              <td className="px-3 py-2 text-right font-bold text-gray-700">{grandQty}</td>
              <td></td><td></td><td></td>
              <td className="px-3 py-2 text-right font-bold text-gray-900 text-base">₹{grandTotal.toLocaleString('en-IN')}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </CardContent>
  </Card>
);

export default InvoiceTable;
